import { Component, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { generateYears } from '@app/utils/sharedFunctions';
import { 
  AbstractControl, 
  FormControl, 
  FormsModule, 
  ReactiveFormsModule, 
  ValidationErrors, 
  ValidatorFn, 
  Validators 
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BiqButtonComponent } from '../biq-button/biq-button.component';
import { UserBiqData } from '@app/schemas/biq';
import { Store } from '@ngrx/store';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, map, startWith, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { BIQ_TOTAL_QUESTIONS } from '@app/utils/constants';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-biq-birth-date',
  imports: [
    ProgressBarComponent,
    BiqButtonComponent,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    AsyncPipe,
    MatCheckboxModule,
  ],
  templateUrl: './birth-date.component.html',
  styleUrl: './birth-date.component.css'
})
export class BirthDateComponent {
  @Output() continueEmitter = new EventEmitter<void>();
  public questNo: number = 2;
  public totalQuestions: number = BIQ_TOTAL_QUESTIONS;
  public years: number[] = [];
  public yearControl = new FormControl<number | null>(null, [Validators.required]);
  public storedBiqData!: UserBiqData;
  public filteredYears!: Observable<number[]>;
  public availableYears: number[] = [];
  public preferNotToSayControl = new FormControl(false);

  constructor(private store: Store<{ userBiq: UserBiqData }>, private cdr: ChangeDetectorRef) {
    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      if (value) {
        if (value.birthYear && value.birthYear > 0) {
          this.yearControl.setValue(value.birthYear);
          this.preferNotToSayControl.setValue(false);
        } 
        // else if (value.birthYear === null) {
        //   this.preferNotToSayControl.setValue(true);
        //   this.yearControl.reset();
        //   this.yearControl.disable();
        // }
      }
    });
  }

  ngOnInit() {
    this.years = generateYears();
    
    this.yearControl.setValidators([
      Validators.required,
      this.validYearValidator(this.years)
    ]);
    this.yearControl.updateValueAndValidity();

    this.preferNotToSayControl.valueChanges.subscribe((preferNotToSay: boolean | null) => {
      if (preferNotToSay) {
        this.yearControl.reset();
        this.yearControl.disable();
      } else {
        this.yearControl.enable();
        // Restore value if it exists in store after re-enabling
        if (this.storedBiqData?.birthYear) {
          this.yearControl.setValue(this.storedBiqData.birthYear);
        }
      }
    });

    this.filteredYears = this.yearControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const results = this._filterYears(value || '');
        return results;
      }),
      tap(results => {
        setTimeout(() => {
          this.availableYears = results;
          this.cdr.markForCheck();
        });
      }),
    );
  }

  private _filterYears(value: string | number): number[] {
    const filterValue = value.toString();
    return this.years.filter(year => year.toString().startsWith(filterValue));
  }

  private validYearValidator(validYears: number[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const inputValue = control.value;
      if (inputValue === null || inputValue === undefined || inputValue === '') {
        return null;
      }
  
      const numericValue = Number(inputValue);
      return validYears.includes(numericValue) ? null : { invalidYear: true };
    };
  }

  onClickContinue() {
    // If "prefer not to say" is checked, we don't need to validate year control
    if (this.preferNotToSayControl.value) {
      const updatedBiq = {
        ...this.storedBiqData,
        birthYear: null, // Use null to indicate "prefer not to say"
      }
      this.store.dispatch(updateUserBiq({ user: updatedBiq }));
      this.continueEmitter.emit();
      return;
    }

    this.yearControl.markAsTouched();
    if (this.yearControl.invalid) return;

    const updatedBiq = {
      ...this.storedBiqData,
      birthYear: this.yearControl.value ? Number(this.yearControl.value) : null,
    }
    this.store.dispatch(updateUserBiq({ user: updatedBiq }));
    this.continueEmitter.emit();
  }
}
